import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { combineLatest, Observable, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingsActions } from 'src/app/core/settings/settings.actions'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { CalendarService } from 'src/app/shared/components/calendar/calendar.service'
import { CalendarEvent } from 'src/app/shared/components/calendar/event'
import { Constants } from 'src/app/shared/constants'
import { Location } from '../../location/location'
import { locationActions } from '../../location/location.actions'
import { locationSelectors } from '../../location/location.selectors'
import { Product } from '../../product/product'
import {
  ProductGroup,
  ProductGroupType
} from '../../product/product-group/product-group'
import { productGroupActions } from '../../product/product-group/product-group.actions'
import { productGroupSelectors } from '../../product/product-group/product-group.selectors'
import { productActions } from '../../product/product.actions'
import { productSelectors } from '../../product/product.selectors'
import { ProductService } from '../../product/product.service'
import { bookingActions } from '../booking.actions'
import { bookingSelectors } from '../booking.selectors'
import { BookingService } from '../booking.service'
import { participationSelectors } from '../participation/participation.selectors'
import { BookingShowModalComponent } from '../show-modal/show.component'
import DEBOUNCED_TIME = Constants.DEBOUNCED_TIME
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'

interface SearchEvent {
  startDate: Date
  endDate: Date
  activeView?: string
}
type bookingIndexTab = 'calendar' | 'list' | 'schedule'
@UntilDestroy()
@Component({
  selector: 'booking-overview',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class BookingOverviewComponent implements OnInit {
  public selectedBookableProductIds$: Observable<number[]>
  public selectedLinkedProductIds$: Observable<number[]>
  public selectedLocationIds$: Observable<number[]>
  public selectedProductGroupIds$: Observable<number[]>
  public form: UntypedFormGroup
  public allProducts$: Observable<Product[]>
  public bookableProducts$: Observable<Product[]>
  public productGroups$: Observable<ProductGroup[]>
  public allowAsLinkedChildProducts$: Observable<Product[]>
  public locations$: Observable<Location[]>
  public calendarEvents$: Observable<CalendarEvent[]>
  public productEntities$: Observable<Dictionary<Product>>
  public filterIsSelected$: Observable<boolean>
  public selectedLanguage$: Observable<string>
  public selectedFilters$: Observable<any>
  public bsModalRef: BsModalRef
  private selectedEvent: any
  public searchBooking$ = new Subject<SearchEvent>()
  public totalProductsSelected: number[] = []
  public bookingModalIsOpen: boolean = false
  private currentCalendarEvents = []
  @Output() openedBookingModal = new EventEmitter<boolean>()
  @Input() selectedType: bookingIndexTab = 'list'
  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private calendarService: CalendarService,
    private productService: ProductService,
    private modalService: BsModalService,
    private changeDetector: ChangeDetectorRef,
    private bookingService: BookingService,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          join: [{ field: 'products' }],
          search: {
            type: ProductGroupType.REPORT_GROUP
          }
        })
      })
    )
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.selectedFilters$ = this.store
      .select(settingSelectors.selectById, 'selectedFilters')
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        tap((item) => {
          this.form.controls.selectedBookableProductIds.setValue(
            item?.selectedBookableProductIds
          )
          this.form.controls.selectedLinkedProductIds.setValue(
            item?.selectedLinkedProductIds
          )
          this.form.controls.selectedLocationIds.setValue(
            item?.selectedLocationIds
          )
          this.form.controls.selectedProductGroupIds.setValue(
            item?.selectedProductGroupIds
          )
        })
      )

    this.form = this.fb.group({
      selectedLinkedProductIds: [null, []],
      selectedLocationIds: [null, []],
      selectedBookableProductIds: [null, []],
      selectedProductGroupIds: [null, []]
    })

    this.selectedBookableProductIds$ =
      this.form.controls.selectedBookableProductIds.valueChanges

    this.selectedLinkedProductIds$ =
      this.form.controls.selectedLinkedProductIds.valueChanges

    this.selectedLocationIds$ =
      this.form.controls.selectedLocationIds.valueChanges
    this.selectedProductGroupIds$ =
      this.form.controls.selectedProductGroupIds.valueChanges

    this.filterIsSelected$ = combineLatest([
      this.selectedBookableProductIds$,
      this.selectedLinkedProductIds$,
      this.selectedLocationIds$,
      this.selectedProductGroupIds$
    ]).pipe(
      withLatestFrom(this.selectedFilters$),
      debounceTime(10),
      tap(
        ([
          [
            selectedBookableProductIds,
            selectedLinkedProductIds,
            selectedLocationIds,
            selectedProductGroupIds
          ],
          selectedFilters
        ]) => {
          const targetFilters = {
            selectedBookableProductIds,
            selectedLinkedProductIds,
            selectedLocationIds,
            selectedProductGroupIds
          }

          if (
            JSON.stringify(targetFilters) != JSON.stringify(selectedFilters)
          ) {
            this.store.dispatch(
              settingsActions.setOneById({
                id: 'selectedFilters',
                value: targetFilters
              })
            )
          }
        }
      ),

      map(([[a, b, c]]) => Boolean(a?.length || b?.length || c?.length)),
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b))
    )

    this.store.dispatch(locationActions.fetchMany({}))
    // this.store.dispatch(productActions.fetchMany({}))

    this.allProducts$ = this.store.select(productSelectors.selectAll)

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.bookableProducts$ = this.allProducts$.pipe(
      map((product) => {
        return product.filter((product) => product.bookable && product.isActive)
      })
    )
    this.productGroups$ = this.store
      .select(productGroupSelectors.selectAll)
      .pipe(
        map((productGroups) =>
          productGroups.filter(
            (productGroup) => productGroup.type == ProductGroupType.REPORT_GROUP
          )
        )
      )

    this.allowAsLinkedChildProducts$ = this.allProducts$.pipe(
      map((product) => {
        return product.filter(
          (product) =>
            (Boolean(product.allowAsLinkedProduct) ||
              Boolean(product.isCourse)) &&
            product.isActive
        )
      })
    )

    this.locations$ = this.store.select(locationSelectors.selectAll)

    this.calendarEvents$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.selectedLinkedProductIds$,
      this.selectedLocationIds$,
      this.selectedBookableProductIds$,
      this.selectedProductGroupIds$,
      this.productEntities$,
      this.selectedFilters$,
      this.searchBooking$
    ])
      .pipe(
        map(
          ([
            bookings,
            selectedLinkedProductIds,
            selectedLocationIds,
            selectedBookableProductIds,
            selectedProductGroupIds,
            productEntities,
            selectedFilters,
            calendarDates
          ]) => {
            if (
              this.isFiltersEmpty(
                selectedLinkedProductIds,
                selectedLocationIds,
                selectedBookableProductIds,
                selectedProductGroupIds
              )
            ) {
              return []
            }
            if (this.bookingModalIsOpen && this.selectedType == 'calendar') {
              return this.currentCalendarEvents
            }
            if (selectedProductGroupIds?.length > 0) {
              this.getGroupProductsId(selectedProductGroupIds).map((pr) => {
                if (productEntities[pr].bookable) {
                  if (
                    selectedBookableProductIds &&
                    !selectedBookableProductIds?.includes(pr)
                  ) {
                    selectedBookableProductIds = [
                      ...selectedBookableProductIds,
                      pr
                    ]
                  } else if (!selectedBookableProductIds) {
                    selectedBookableProductIds = [pr]
                  }
                } else {
                  if (
                    selectedLinkedProductIds &&
                    !selectedLinkedProductIds?.includes(pr)
                  ) {
                    selectedLinkedProductIds = [...selectedLinkedProductIds, pr]
                  } else if (!selectedLinkedProductIds) {
                    selectedLinkedProductIds = [pr]
                  }
                }
              })
            }
            let endDate
            if (calendarDates?.activeView == 'month') {
              endDate = moment(calendarDates?.endDate).add(1, 'day').toDate()
            } else {
              endDate = calendarDates?.endDate
            }
            const filteredItems = bookings.filter(
              (booking) =>
                moment(booking.startDate).valueOf() >=
                  calendarDates?.startDate.valueOf() &&
                moment(booking.endDate).valueOf() <= endDate.valueOf() &&
                booking.hasOwnProperty('id') &&
                booking.id
            )

            const expectedSuccessCount = [
              selectedLinkedProductIds?.length,
              selectedLocationIds?.length,
              selectedBookableProductIds?.length
            ].filter(Boolean)?.length

            const filtered = filteredItems.filter((booking) => {
              let successCount = 0
              if (
                this.isSelectedBookableProductIds(
                  selectedBookableProductIds,
                  booking
                )
              ) {
                successCount++
              }

              if (
                this.isSelectedLinkedProductIds(
                  selectedLinkedProductIds,
                  booking
                )
              ) {
                successCount++
              }
              if (
                this.isSelectedLocationIds(
                  selectedLocationIds,
                  productEntities,
                  booking
                )
              ) {
                successCount++
              }
              return successCount > 0 && successCount <= expectedSuccessCount
            })

            const bookingWPotentialAct = filteredItems.filter(
              (booking) =>
                (booking.linkedProductId === 0 &&
                  booking.allowedLinkedProducts?.length > 0 &&
                  this.isAllowedLinkedProducts(
                    booking,
                    selectedLinkedProductIds,
                    selectedLocationIds,
                    selectedBookableProductIds,
                    selectedProductGroupIds
                  )) ||
                (booking.linkedProductId === null &&
                  booking.allowedLinkedProducts?.length > 0 &&
                  this.isBookingProductAllowed(
                    booking,
                    selectedLinkedProductIds
                  ))
            )

            let dependantProducts = []

            const producutIdsAllBookings = [
              ...new Set(
                [...new Set([...filtered, ...bookingWPotentialAct])].map(
                  (bk) => bk.productId
                )
              )
            ]
            producutIdsAllBookings?.map((pr) => {
              if (productEntities[pr]?.dependantProducts?.length > 0) {
                dependantProducts = [
                  ...dependantProducts,
                  ...productEntities[pr].dependantProducts
                ]
              }
            })

            let bookingsWithDependantsProduct = []
            if (
              selectedLinkedProductIds?.length > 0 ||
              selectedLocationIds?.length > 0 ||
              selectedBookableProductIds?.length > 0 ||
              selectedProductGroupIds?.length > 0
            ) {
              filteredItems
                .map((booking) => {
                  let haveDependant = false
                  if (
                    booking.productId &&
                    productEntities[booking.productId]?.dependantProducts &&
                    (selectedProductGroupIds?.filter((item) =>
                      productEntities[
                        booking.productId
                      ]?.dependantProducts.includes(item)
                    )?.length > 0 ||
                      selectedBookableProductIds?.filter((item) =>
                        productEntities[
                          booking.productId
                        ]?.dependantProducts.includes(item)
                      )?.length > 0)
                  ) {
                    haveDependant = true
                  }

                  if (
                    booking.linkedProductId &&
                    productEntities[booking.linkedProductId]
                      ?.dependantProducts &&
                    (selectedProductGroupIds?.filter((item) =>
                      productEntities[
                        booking.linkedProductId
                      ]?.dependantProducts.includes(item)
                    )?.length > 0 ||
                      selectedBookableProductIds?.filter((item) =>
                        productEntities[
                          booking.linkedProductId
                        ]?.dependantProducts.includes(item)
                      )?.length > 0)
                  ) {
                    haveDependant = true
                  }
                  if (haveDependant) {
                    bookingsWithDependantsProduct.push({
                      ...booking,
                      isDependant:
                        productEntities[booking.productId]?.dependantProducts
                          ?.length > 0 ||
                        productEntities[booking.linkedProductId]
                          ?.dependantProducts?.length > 0
                          ? true
                          : false,
                      dependantProducts:
                        productEntities[booking.productId]?.dependantProducts ??
                        productEntities[booking.linkedProductId]
                          ?.dependantProducts
                    })
                  }
                })
                .filter((x) => Boolean(x))
                .flat()
            }

            this.currentCalendarEvents = [
              ...filtered,
              ...bookingWPotentialAct,
              ...bookingsWithDependantsProduct
            ].reduce((uniqueEvents, event) => {
              if (!uniqueEvents.some((e) => e.id === event.id)) {
                uniqueEvents.push(event)
              }
              return uniqueEvents
            }, [])

            return this.currentCalendarEvents
          }
        ),
        withLatestFrom(
          this.store.select(productSelectors.selectEntities),
          this.store.select(participationSelectors.selectEntities),
          this.store.select(bookingSelectors.selectEntities),
          this.searchBooking$,
          this.selectedBookableProductIds$,
          this.selectedProductGroupIds$,
          this.store.select(productGroupSelectors.selectEntities)
        ),
        map(
          ([
            bookings,
            productEntities,
            participationEntities,
            bookingEntities,
            calendarDates,
            selectedBookableProductIds,
            selectedProductGroupIds,
            productGroupEntities
          ]: any) => {
            let selectedProducts = 0
            selectedProductGroupIds = selectedProductGroupIds ?? []
            let targetedProductGroupProductIds = []
            selectedProductGroupIds?.map(
              (productGroupId) =>
                (targetedProductGroupProductIds = [
                  ...targetedProductGroupProductIds,
                  ...(productGroupEntities[productGroupId]?.products ?? [])
                ])
            )
            const targetedProductsIds = [
              ...new Set([
                ...(selectedBookableProductIds ?? []),
                ...(targetedProductGroupProductIds ?? [])
              ])
            ]
            selectedProducts = targetedProductsIds?.length
            let bookingWPotentialAct = bookings.filter(
              (booking) =>
                (booking.linkedProductId === 0 &&
                  booking.allowedLinkedProducts?.length > 0) ||
                (booking.linkedProductId === null &&
                  booking.allowedLinkedProducts?.length > 0)
            )

            const bookingWPotentialActIds: any[] = bookingWPotentialAct.map(
              (bk) => bk.id
            )

            let bookingWithOutPotentialAct = bookings.filter(
              (bk: any) => !bookingWPotentialActIds.includes(bk.id)
            )

            let bookingWithDependantMapped = []
            if (
              calendarDates.activeView == 'weeklySchedule' ||
              calendarDates.activeView == 'week'
            ) {
              const filteredItems = bookings.filter(
                (booking) =>
                  targetedProductsIds.includes(booking.productId) ||
                  targetedProductsIds.includes(booking.linkedProductId)
              )
              let mainFilteredValue = filteredItems.map((booking) => {
                return {
                  id: booking.id,
                  startDate: booking.startDate,
                  endDate: booking.endDate,
                  name: `${
                    productEntities[booking.linkedProductId]?.description ?? ''
                  } ${productEntities[booking.productId]?.description} - `,
                  isAvailable: true,
                  maxParticipants: booking.maxParticipants || 0,
                  availableParticipantCount:
                    booking.availableParticipantCount || 0,
                  currentParticipantCount: booking.currentParticipantCount || 0,
                  bookableProductId: booking.productId,
                  bookableProductDescription:
                    productEntities[booking.productId]?.description,
                  linkedProductId: booking.linkedProductId,
                  productId: booking.linkedProductId,
                  linkedProductDescription:
                    productEntities[booking.linkedProductId]?.description,
                  group: booking.linkedProductId ?? booking.productId,
                  dependantProducts:
                    productEntities[booking.productId]?.dependantProducts,
                  isDependant: false
                }
              })
              mainFilteredValue = [...mainFilteredValue]
              const groupFilteredValue = mainFilteredValue
                .map((data) => {
                  if (data?.dependantProducts?.length > 0) {
                    return data.dependantProducts.map((products) => {
                      return {
                        id: data.id,
                        startDate: data.dependantProducts.includes(products)
                          ? data.startDate
                          : null,
                        endDate: data.dependantProducts.includes(products)
                          ? data.endDate
                          : null,
                        name: `${
                          productEntities[products]?.description ?? ''
                        } ${productEntities[products]?.description}`,
                        isAvailable: true,
                        bookableProductId: data.bookableProductId,
                        productId: products,
                        maxParticipants: data.maxParticipants || 0,
                        availableParticipantCount:
                          data.availableParticipantCount || 0,
                        currentParticipantCount:
                          data.currentParticipantCount || 0,
                        bookableProductDescription:
                          productEntities[products]?.description,
                        linkedProductId: products,
                        linkedProductDescription:
                          productEntities[products]?.description,
                        group: products,
                        isDependant: true
                      }
                    })
                  }
                })
                .filter((data) => data !== undefined)
              bookingWithDependantMapped = groupFilteredValue.map((bk: any) => {
                return this.calendarService.mapBookingToCalEvent(
                  bk[0],
                  productEntities[bk[0].bookableProductId],
                  productEntities[bk[0].linkedProductId],
                  null,
                  `${
                    productEntities[bk[0].linkedProductId]?.description ?? ''
                  } ${
                    productEntities[bk[0].bookableProductId]?.description ?? ''
                  }`
                )
              })
            }

            const bookingMapped = bookingWithOutPotentialAct.map((booking) => {
              return this.calendarService.mapBookingToCalEvent(
                booking,
                productEntities[booking.productId],
                productEntities[booking.linkedProductId],
                booking.participations?.map(
                  (pId) => participationEntities[pId]
                ),
                `${
                  productEntities[booking.linkedProductId]?.description ?? ''
                } ${productEntities[booking.productId]?.description ?? ''}`
              )
            })

            let bookingMappedWPotentialAct = []
            bookingWPotentialAct.map((bk) => {
              return bk.allowedLinkedProducts.map((bkaux) => {
                bookingMappedWPotentialAct = [
                  ...bookingMappedWPotentialAct,
                  this.calendarService.mapBookingToCalEvent(
                    bk,
                    productEntities[bk.productId],
                    productEntities[bk.linkedProductId],
                    bk.participations?.map((pId) => participationEntities[pId]),
                    `${productEntities[bkaux]?.description ?? ''}</br> ${
                      productEntities[bk.linkedProductId]?.description ?? ''
                    } ${productEntities[bk.productId]?.description ?? ''}`,
                    bkaux
                  )
                ]
              })
            })
            const partialBookingToShow = [
              ...bookingMapped,
              ...bookingMappedWPotentialAct
            ]
            bookingWithDependantMapped.forEach((item) => {
              if (!partialBookingToShow.find((it) => item.id == it.id)) {
                partialBookingToShow.push(item)
              }
            })

            const totalBookingToShow =
              selectedProducts > 1 &&
              (calendarDates.activeView == 'month' ||
                calendarDates.activeView == 'week')
                ? [...partialBookingToShow].filter(
                    (bk) => !bk?.params?.booking?.isDependant
                  )
                : [...partialBookingToShow]

            return totalBookingToShow
          }
        )
      )
      .pipe(
        tap(() => {
          this.totalProductsSelected = this.getSelectedProducts()
        })
      )

    combineLatest([
      this.searchBooking$,
      this.selectedBookableProductIds$,
      this.selectedLinkedProductIds$,
      this.selectedLocationIds$,
      this.selectedProductGroupIds$
    ])
      .pipe(untilDestroyed(this), debounceTime(DEBOUNCED_TIME))
      .subscribe(
        ([
          event,
          productIds,
          linkedProductIds,
          locationIds,
          ProductGroupIds
        ]) => {
          console.log(
            `Will fetch bookings from range ${event?.startDate?.toISOString()} - ${event?.endDate?.toISOString()}`
          )
          if (ProductGroupIds?.length > 0) {
            let productEntities
            this.productEntities$
              .pipe(take(1))
              .subscribe((pr) => (productEntities = pr))
            this.getGroupProductsId(ProductGroupIds).map((pr) => {
              if (productEntities[pr].bookable) {
                if (productIds && !productIds?.includes(pr)) {
                  productIds = [...productIds, pr]
                } else if (!productIds) {
                  productIds = [pr]
                }
              } else {
                if (linkedProductIds && !linkedProductIds?.includes(pr)) {
                  linkedProductIds = [...linkedProductIds, pr]
                } else if (!linkedProductIds) {
                  linkedProductIds = [pr]
                }
              }
            })
          }
          let endDate
          if (event.activeView == 'month') {
            endDate = moment(event?.endDate).add(1, 'day').toDate()
          } else {
            endDate = event?.endDate
          }
          // if have any selection we need load rest of bookings
          if (productIds?.length > 0 || linkedProductIds?.length > 0) {
            const totalProdutIds = [
              ...(productIds ?? []),
              ...(linkedProductIds ?? [])
            ]
            const params = RequestQueryBuilder.create({
              search: {
                id: {
                  [CondOperator.IN]: totalProdutIds
                }
              },

              join: [{ field: 'dependantProducts' }]
            })
            this.productService
              .getMany(params)
              .pipe(take(1))
              .subscribe((requesProducts) => {
                requesProducts?.data?.forEach((pr) => {
                  if (
                    productIds?.includes(pr.id) &&
                    pr?.dependantProducts?.length > 0
                  ) {
                    productIds = [
                      ...productIds,
                      ...pr.dependantProducts.map((item) => item.id)
                    ]
                  }
                  if (
                    linkedProductIds?.includes(pr.id) &&
                    pr?.dependantProducts?.length > 0
                  ) {
                    linkedProductIds = [
                      ...linkedProductIds,
                      ...pr.dependantProducts.map((item) => item.id)
                    ]
                  }
                })
                this.fetchBookings(
                  event,
                  endDate,
                  productIds,
                  linkedProductIds,
                  locationIds
                )
              })
          } else {
            this.fetchBookings(
              event,
              endDate,
              productIds,
              linkedProductIds,
              locationIds
            )
          }

          // if it has activity or product => fetch once
        }
      )
  }
  private fetchBookings(
    event,
    endDate,
    productIds,
    linkedProductIds,
    locationIds
  ) {
    if (
      (!productIds || productIds?.length == 0) &&
      (!linkedProductIds || linkedProductIds?.length == 0) &&
      (!locationIds || locationIds?.length == 0) &&
      (!this.form?.controls?.selectedProductGroupIds?.value ||
        this.form?.controls?.selectedProductGroupIds?.value?.length == 0)
    ) {
      return
    }
    this.store.dispatch(bookingActions.resetState())
    this.store.dispatch(
      bookingActions.fetchMany({
        params: RequestQueryBuilder.create({
          join: [
            { field: 'participations' },
            { field: 'product' },
            { field: 'linkedProduct' },
            { field: 'product.dependantProducts' }
          ],
          search: {
            startDate: {
              [CondOperator.GREATER_THAN_EQUALS]: moment(
                event?.startDate
              ).format('YYYY-MM-DD 00:00:00') // temporary solution to timezoning and time event handling
            },
            endDate: {
              [CondOperator.LOWER_THAN_EQUALS]: moment(endDate).format(
                'YYYY-MM-DD 23:59:59'
              ) // temporary solution to timezoning and time event handling
            },
            $or: [
              productIds?.length
                ? {
                    productId: {
                      [CondOperator.IN]: productIds
                    }
                  }
                : null,

              linkedProductIds?.length
                ? {
                    linkedProductId: {
                      [CondOperator.IN]: linkedProductIds
                    }
                  }
                : null,

              locationIds?.length
                ? {
                    'product.locationId': {
                      [CondOperator.IN]: locationIds
                    }
                  }
                : null,
              locationIds?.length
                ? {
                    'linkedProduct.locationId': {
                      [CondOperator.IN]: locationIds
                    }
                  }
                : null
            ].filter((c) => Boolean(c))
          }
        })
      })
    )
  }

  private isAllowedLinkedProducts(
    booking,
    selectedLinkedProductIds,
    selectedLocationIds,
    selectedBookableProductIds,
    selectedProductGroupIds
  ) {
    if (
      selectedLinkedProductIds?.length === 0 &&
      selectedBookableProductIds?.length === 0 &&
      selectedLocationIds?.length === 0 &&
      selectedProductGroupIds?.length === 0
    ) {
      return false
    }
    const allProductIds = [
      ...new Set([...selectedLinkedProductIds, ...selectedBookableProductIds])
    ]
    let isIncluded: boolean = false
    booking.allowedLinkedProducts.map((pr) => {
      if (allProductIds.includes(pr)) {
        isIncluded = true
      }
    })
    return isIncluded
  }
  private getGroupProductsId(selectedProductGroupIds) {
    let productGroupEntities
    this.store
      .select(productGroupSelectors.selectEntities)
      .pipe(take(1))
      .subscribe((grp) => (productGroupEntities = grp))
    selectedProductGroupIds = selectedProductGroupIds ?? []
    let targetedProductGroupProductIds = []
    if (Object.keys(productGroupEntities)?.length > 0) {
      selectedProductGroupIds?.map(
        (productGroupId) =>
          (targetedProductGroupProductIds = [
            ...targetedProductGroupProductIds,
            ...(productGroupEntities[productGroupId]?.products ?? [])
          ])
      )
    }

    return targetedProductGroupProductIds
  }
  private isBookingProductAllowed(booking, selectedLinkedProductIds): boolean {
    return (
      booking.allowedLinkedProducts.filter(
        (allowedProduct) =>
          allowedProduct ===
          selectedLinkedProductIds?.find(
            (selected) => allowedProduct === selected
          )
      )?.length > 0
    )
  }

  public onChangeDates(event: SearchEvent) {
    this.selectedEvent = event
    this.searchBooking$.next(event)
  }

  onClickEvent(event: CalendarEvent) {
    this.openedBookingModal.emit(true)
    this.bookingModalIsOpen = true
    this.bsModalRef = this.modalService.show(BookingShowModalComponent, {
      initialState: {
        bookingId: event.params.booking.id
      },
      class: 'modal-xxl modal-dialog-scrollable'
    })

    this.bsModalRef.onHide.pipe(take(1)).subscribe((modal) => {
      this.store.dispatch(
        BreadCrumbActions.initHistory({
          pageName: 'bookingModule.bookings',
          url: `/bookings`,
          titleName: ['bookingModule.bookings', 'general.calendar']
        })
      )
      this.openedBookingModal.emit(false)
      this.bookingModalIsOpen = false
      if (this.bsModalRef?.content?.activeTab != 'supervisors') {
        this.searchBooking$.next({
          startDate: moment(this.selectedEvent.startDate).toDate(),
          endDate: moment(this.selectedEvent.endDate).toDate(),
          activeView: this.selectedEvent.activeView
        })
      }
    })
  }

  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }

  public onSearchLocation(term: string) {
    this.store.dispatch(
      locationActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }
  public getSelectedProducts(): any[] {
    const totalProductsIds = [
      ...(this.form?.controls['selectedBookableProductIds']?.value ?? []),
      ...(this.form?.controls['selectedLinkedProductIds']?.value ?? []),
      ...(this.getGroupProductsId(
        this.form?.controls['selectedProductGroupIds']?.value
      ) ?? [])
    ]

    return [...new Set([...totalProductsIds])]
  }
  private isSelectedLocationIds(selectedLocationIds, productEntities, booking) {
    return (
      selectedLocationIds?.length &&
      (selectedLocationIds.includes(
        productEntities[booking.productId]?.locationId
      ) ||
        selectedLocationIds.includes(
          productEntities[booking.linkedProductId]?.locationId
        ))
    )
  }

  private isSelectedLinkedProductIds(selectedLinkedProductIds, booking) {
    const valueMapped =
      selectedLinkedProductIds?.length &&
      (selectedLinkedProductIds.includes(booking.linkedProductId) ||
        (!booking.linkedProductId &&
          booking.allowedLinkedProducts?.some((a) =>
            selectedLinkedProductIds.includes(a)
          )))

    return valueMapped
  }

  private isSelectedBookableProductIds(selectedBookableProductIds, booking) {
    return (
      selectedBookableProductIds?.length &&
      selectedBookableProductIds.includes(booking.productId)
    )
  }

  private isFiltersEmpty(
    selectedLinkedProductIds,
    selectedLocationIds,
    selectedBookableProductIds,
    selectedProductGroupIds
  ) {
    return (
      !selectedLinkedProductIds?.length &&
      !selectedLocationIds?.length &&
      !selectedBookableProductIds?.length &&
      !selectedProductGroupIds
    )
  }
}
